import React from "react";
import moment from "moment";
import { useQuery } from "@tanstack/react-query";
import { getSellerContact } from "@/services/sellerContactService";
import "./SellerContactCard.scss";

export const sellerContactKey = (inventoryId) => ["sellerContact", Number(inventoryId)];

const PREFERS = { Call: "a call", Text: "a text", Email: "an email" };

const usDigits = (value) => {
  const digits = String(value || "").replace(/\D/g, "");
  if (digits.length === 10) return digits;
  return digits.length === 11 && digits[0] === "1" ? digits.slice(1) : "";
};

const formatPhone = (value) => {
  const digits = usDigits(value);
  return digits ? `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}` : value;
};

const SellerContactCard = ({ inventoryId }) => {
  const query = useQuery({
    queryKey: sellerContactKey(inventoryId),
    queryFn: () => getSellerContact(inventoryId),
    enabled: Boolean(inventoryId),
    retry: false,
  });
  const contact = query.data;
  if (!contact || (!contact.name && !contact.email && !contact.phone)) return null;

  const request = contact.request;
  const digits = usDigits(contact.phone);
  const meta = [
    request?.prefers ? `Prefers ${PREFERS[request.prefers] || request.prefers}` : null,
    request?.submittedAt ? `Asked ${moment.utc(request.submittedAt).local().format("MMM D")} from your Gitcha\u00a0bio` : null,
  ].filter(Boolean);

  return (
    <section className="seller-contact" aria-label="Seller contact">
      <div className="seller-contact__head">
        <div className="seller-contact__who">
          <h2 className="seller-contact__name">{contact.name || "Your client"}</h2>
          {meta.length ? <p className="seller-contact__meta">{meta.join(" · ")}</p> : null}
        </div>
        <div className="seller-contact__actions">
          {digits ? (
            <>
              <a className="seller-contact__action" href={`tel:+1${digits}`}>
                Call
              </a>
              <a className="seller-contact__action" href={`sms:+1${digits}`}>
                Text
              </a>
            </>
          ) : null}
          {contact.email ? (
            <a className="seller-contact__action" href={`mailto:${encodeURIComponent(contact.email)}`}>
              Email
            </a>
          ) : null}
        </div>
      </div>
      <dl className="seller-contact__rows">
        {contact.phone ? (
          <div>
            <dt>Phone</dt>
            <dd>{formatPhone(contact.phone)}</dd>
          </div>
        ) : null}
        {contact.email ? (
          <div>
            <dt>Email</dt>
            <dd>{contact.email}</dd>
          </div>
        ) : null}
        {(request?.rows || []).map((row) => (
          <div key={row.label}>
            <dt>{row.label}</dt>
            <dd>{row.value}</dd>
          </div>
        ))}
      </dl>
      {request?.note ? <blockquote className="seller-contact__note">{request.note}</blockquote> : null}
      {!request && contact.notes ? <p className="seller-contact__notes">{contact.notes}</p> : null}
    </section>
  );
};

export default SellerContactCard;
